import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { RawMaterialDtoExtend } from "@root/user/src/app/view/neq/pages/rawmaterials/rawmaterials-list.page";
import { NeqSelectAutocompleteComponent } from "@sharedcommon/components/select-autocomplete/neq-select-autocomplete.component";
import { MultiEditError } from "@sharedcommon/model/multi-edit-error.model";
import { URN } from "@sharedcommon/model/urn.model";
import { RawMaterialDto, RawMaterialNutrientDto } from "@sharedcommon/rest/data.restservice";
import { Constants } from "@sharedcommon/util/constants.util";
import { PrimengTableCols } from "@sharedcommon/interfaces/primeng-table-cols";
import { RawMaterialsMultiEditService } from "@usercommon/service/rawmaterials-multi-edit.service";
import { Observable } from "rxjs";

@Component({
    selector: 'neq-primeng-table-cell-inputs-rawmaterial',
    templateUrl: './primeng-table-cell-inputs-rawmaterial.component.html',
    styleUrls: ['./primeng-table-cell-inputs-rawmaterial.component.scss']
})
export class PrimengTableCellInputsRawmaterialComponent implements OnInit {
    /**
     * Column of the current cell
     */
    @Input() col: PrimengTableCols;

    /**
     * Line of the current raw mat
     */
    @Input() rowData: RawMaterialDto;

    /**
     * Event when data was checked
     */
    @Input() eventDataChecked: Observable<void>;

    /**
     * Initial data of the raw mat
     */
    @Input() initalRawMaterialsData: RawMaterialDtoExtend[];

    /**
     * Nutrients data for the raw mat
     */
    @Input() nutrientsData: any[];

    /**
     * Initial nutrients data for raw mat
     */
    @Input() initNutrientsData: any[];

    /**
     * Event when a data change on the raw mat
     */
    @Output() changeDataEmitter = new EventEmitter<any>();

    /**
     * The auto complete select
     */
    @ViewChild('select') select: NeqSelectAutocompleteComponent;

    private isCode: boolean;
    private isEntity: boolean;
    private isNote: boolean;
    private isNutrient: boolean;
    private isReference: boolean;
    private multiEditError: MultiEditError;
    private previousRawMat: RawMaterialDtoExtend;
    private intialNutrientValue: number = null;

    hasChange: boolean = false;
    ngModel: any;
    currentNutrient: RawMaterialNutrientDto = null;

    rawmaterialForm = new FormGroup({
        code: new FormControl(null, [
          Validators.required,
          Validators.pattern(Constants.REGEX_ALL_EXCEPT_SPACES_AND_GENERIC_CURRENCY_SYMBOL),
          Validators.maxLength(Constants.MAX_LENGTH_FOR_RM_CODE)
        ]),
        label: new FormControl(null, [Validators.required, Validators.maxLength(Constants.MAX_LENGTH_FOR_RM_LABEL)]),
        referenceLabel: new FormControl(null, Validators.required),
        entityLabel: new FormControl(null, Validators.required),
        notes: new FormControl()
    });


    constructor(
        private readonly rawMaterialsMultiEditService: RawMaterialsMultiEditService,
        private readonly translate: TranslateService
    ) { }

    ngOnInit() {
        // Event subscribe
        this.eventDataChecked.subscribe(_ => this.codeEntityCheckedVerif());

        // Check error
        if (this.rawMaterialsMultiEditService.multiEditErrors) {
            this.codeEntityCheckedVerif()
        } else {
            this.multiEditError = new MultiEditError();
        }

        // Init bool
        this.isCode = this.col.field === "code";
        this.isEntity = this.col.field === "entityLabel";
        this.isReference = this.col.field === "referenceLabel";
        this.isNote = this.col.field === "notes"
        this.isNutrient = !this.isCode && !this.isEntity && !this.isReference && !this.isNote && this.col.field !== "calculationStatus" && this.col.field !== "label" ;

        // Inital data
        this.previousRawMat = this.initalRawMaterialsData.find(data => data.id === this.rowData.id);

        if (this.isEntity) {
            // Init ngModel for select
            if (this.rowData.entity == null || this.rowData.entityLabel == null) {
                this.ngModel = null;
            } else {
                this.ngModel = { id: this.rowData.entity.identifier, label: `${this.rowData.entity.identifier} - ${this.rowData.entityLabel}` };
            }
        } else if (this.isReference) {
            // Init ngModel for select
            if (this.rowData.referenceId == null || this.rowData.referenceLabel == null) {
                this.ngModel = null;
            } else {
                const ref = this.col.options.find(opt => opt.id === this.rowData.referenceId);
                this.ngModel = { id: this.rowData.referenceId, label: `${ref.code} - ${ref.label}` };
            }

            // Init the previous value
            let reference = this.col.options.find(ref => ref.id == this.previousRawMat.referenceId);
            this.previousRawMat.referenceLabel = reference.code + " - " + reference.label;
        } else if(this.isNutrient) {
            // Save the inital value of the nutrient
            let nutrient = this.nutrientsData[this.rowData.id].find(nut => nut.nutrientCode == this.col.field);

            if (nutrient) {
                this.currentNutrient = nutrient;
                this.intialNutrientValue = this.initNutrientsData.find(data => data.rmId == this.rowData.id && data.code == nutrient.nutrientCode).value;
            }
        }
    }

    /**
     * Get the tooltip message
     * @param inputFormCtrl the form conrtol
     * @returns the tooltip message
     */
    getTooltipMessage(inputFormCtrl: FormControl) {
        let message = '';
        this.hasChange = false;

        if (this.isCode || this.isEntity) {
            // Custom set value for entitylabel form control because autocomplete select
            if (this.isEntity) {
                inputFormCtrl.setValue(this.rowData.entityLabel);

                // Check and set error after verification
                if (this.multiEditError.hasReferenceError && inputFormCtrl.value) {
                    inputFormCtrl.setErrors({ 'referenceMatch': true });
                    inputFormCtrl.markAsDirty();
                }
            }

            // Add error on form
            if (this.multiEditError.hasCodeEntityError() && inputFormCtrl.value) {
                inputFormCtrl.setErrors({ 'unique': true });
                inputFormCtrl.markAsDirty();
            }

            const errors = inputFormCtrl.errors;

            // Set the error message
            if (errors) {
                inputFormCtrl.markAsDirty();
                if (errors.required) {
                    message = this.translate.instant('error.common.field-required');
                } else if (errors.maxlength) {
                    message = this.translate.instant('error.common.text.max', {param : Constants.MAX_LENGTH_FOR_RM_CODE});
                } else if (errors.pattern) {
                    message = this.translate.instant('error.common.text.character.invalid');
                } else if (errors.unique) {
                    // Check if have error
                    if (this.multiEditError.hasCodeEntityError())
                        message = this.translate.instant('error.common.code.unique');
                    // else clear error
                    else
                        inputFormCtrl.updateValueAndValidity();
                } else if (errors.referenceMatch) {
                    message = this.translate.instant('error.common.reference-match');
                }
            }

            // Add previous value message
            if (this.isCode && this.previousRawMat && this.rowData.code !== this.previousRawMat.code) {
                message = this.previousValueTooltip(message, this.previousRawMat.code);
            } else if (this.isEntity && (this.rowData.entity == null || this.rowData.entity.identifier !== this.previousRawMat.entity.identifier)) {
                message = this.previousValueTooltip(message, `${this.previousRawMat.entity.identifier} - ${this.previousRawMat.entityLabel}`);
            }
        } else if (this.isNote) {
            // Add previous value message
            if (this.previousRawMat && this.rowData.notes !== this.previousRawMat.notes) {
                message = this.previousValueTooltip(message, this.previousRawMat.notes);
            }
        } else {
            // Custom set value for referenceLabel form control because autocomplete select
            if (this.isReference) {
                inputFormCtrl.setValue(this.rowData.referenceLabel);

                // Check and set error after verification
                if (inputFormCtrl.value && this.multiEditError.hasReferenceError) {
                    inputFormCtrl.setErrors({ 'referenceMatch': true });
                    inputFormCtrl.markAsDirty();
                }
            }

            const errors = inputFormCtrl.errors;

            // Set the error message
            if (errors) {
                if (errors.required) {
                    message = this.translate.instant('error.common.field-required');
                } else if (errors.maxlength) {
                    message = this.translate.instant('error.common.text.max', {param : Constants.MAX_LENGTH_FOR_RM_LABEL});
                } else if (errors.referenceMatch) {
                    message = this.translate.instant('error.common.reference-match');
                }
            }

            // Add previous value message
            if (this.isReference && this.previousRawMat && this.rowData.referenceId !== this.previousRawMat.referenceId) {
                message = this.previousValueTooltip(message, this.previousRawMat.referenceLabel);
            } else if (!this.isReference && this.previousRawMat && this.rowData[this.col.field] !== this.previousRawMat[this.col.field]) {
                message = this.previousValueTooltip(message, this.previousRawMat[this.col.field]);
            }
        }
        return message;
    }

    /**
     * Get tooltip message for nutrient
     * @returns the message
     */
    getTooltipNutrient() {
        if (this.intialNutrientValue !== this.currentNutrient.value) {
            return this.previousValueTooltip("", this.intialNutrientValue ? this.intialNutrientValue.toString() : null);
        } else {
            this.hasChange = false;
        }
        return null;
    }

    /**
     * Launch change data with the current raw mat
     */
    onChangeData() {
        this.changeDataEmitter.emit(this.rowData);
    }


    /**
     * Launch change data for a nutrient change
     */
    onNutrientChange() {
        this.changeDataEmitter.emit('nutrient');
    }

    /**
     * Fetch data for auto complete select
     */
    fetchData = (searchTerm, _page) => {
        if (this.isEntity || this.isReference) {
            return Promise.resolve(
              this.col.options
                .filter(opt =>
                  opt.code.includes(searchTerm) ||
                  (
                    (this.isEntity && opt.label.toLowerCase().includes(searchTerm.toLowerCase())) ||
                    (this.isReference && opt.label.toLowerCase().includes(searchTerm.toLowerCase()))
                  ))
                .map(res => ({
                  id: res.id,
                  label: `${res.code} - ${this.isEntity ? res.name : res.label}`
                }))
                .sort((a, b) => a.label.localeCompare(b.label))
            );
        }
        return null;
    };

    /**
     * Change data after auto select change
     * @param event the selected element
     */
    changeSelectedElement(event: any) {
        if (this.isEntity) {
            if (event) {
                const selectedEntity = this.col.options.find(c => c.id == event.id);

                const urnEntity = new URN();
                urnEntity.identifier = selectedEntity.code;
                this.rowData.entity = urnEntity;
                this.rowData.entityLabel = selectedEntity.name;
            } else {
                this.rowData.entity = null;
                this.rowData.entityLabel = null;
            }
        } else if (this.isReference) {
            if (event) {
                this.rowData.referenceId = event.id;
                this.rowData.referenceLabel = event.label;
            } else {
                this.rowData.referenceId = null;
                this.rowData.referenceLabel = null;
            }
        }
        this.onChangeData();
    }

    /**
     * Change model after code/entity was checked
     */
    private codeEntityCheckedVerif() {
        this.multiEditError = this.rawMaterialsMultiEditService.multiEditErrors.find(r => r.rawMatId == this.rowData.id);

        // Init ngModel for select
        if (this.isEntity && this.rowData.entity != null) {
            this.ngModel = { id: this.rowData.entity.identifier, label: `${this.rowData.entity.identifier} - ${this.rowData.entityLabel}` };
        } else if (this.isReference && this.rowData.referenceId != null) {
            const ref = this.col.options.find(o => o.id === this.rowData.referenceId);
            this.ngModel = { id: this.rowData.referenceId, label: `${ref.code} - ${ref.label}` };
        }
    }

    /**
     * Add previous value on the tootlip message
     * @param message the tootltip message
     * @param previousValue the previous value
     * @returns the new tootlip message
     */
    private previousValueTooltip(message: string, previousValue: string) {
        if (previousValue == null || previousValue == '') {
            previousValue = this.translate.instant('label.common.empty')
        }

        // Add new line
        if (message != '') {
            message += '\n';
        }

        message += this.translate.instant('msg.common.previous-value') + previousValue;
        this.hasChange = true;

        return message;
    }
}
